import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import type { CardScope } from './credit'
import {
  FINANCE_BANK_STATEMENT_DELETE,
  FINANCE_BANK_STATEMENT_DOWNLOAD,
  FINANCE_BANK_STATEMENT_READ,
  FINANCE_BANK_STATEMENT_UPLOAD,
} from './permissions'

/**
 * Bankadan gelen ekstre dosyaları (sunucuda şifreli saklanır). Kişisel kart/kredinin ekstresini yalnız sahibi,
 * şirketinkini finance:bank_statement:* yetkisi olan görür; dosya her açıldığında denetim kaydına yazılır.
 */
export type StatementTarget = 'card' | 'loan' | 'cash_advance' | 'account'

export type BankStatement = {
  id: string
  period: string
  statementDate: string
  dueDate: string
  bankTotal: string | null
  bankMin: string | null
  note: string
  fileName: string
  contentType: string
  size: number
  createdAt: string
  systemTotal: string | null
  systemMin: string | null
  diffTotal: string | null
  diffMin: string | null
  /** null: karşılaştırılacak tutar yok. */
  match: boolean | null
}

export type StatementPerms = { read: boolean; download: boolean; upload: boolean; delete: boolean }

/** Kişisel kayıtta sahibi her şeyi yapar; şirkette her işlem ayrı yetki. */
export function useStatementPerms(scope: CardScope): StatementPerms {
  const { can } = useAuth()
  if (scope === 'personal') return { read: true, download: true, upload: true, delete: true }
  const read = can(FINANCE_BANK_STATEMENT_READ)
  return {
    read,
    download: read && can(FINANCE_BANK_STATEMENT_DOWNLOAD),
    upload: read && can(FINANCE_BANK_STATEMENT_UPLOAD),
    delete: read && can(FINANCE_BANK_STATEMENT_DELETE),
  }
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const opt = (v: unknown) => (v == null || v === '' ? null : String(v))

const key = (type: StatementTarget, id: string) => ['finance', 'bank-statements', type, id] as const

export const useBankStatements = (type: StatementTarget, id: string, enabled = true) =>
  useQuery({
    queryKey: key(type, id),
    enabled: enabled && !!id,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/bank-statements', { target_type: type, target_id: id })).map(
        (r): BankStatement => ({
          id: s(r.id),
          period: s(r.period),
          statementDate: s(r.statement_date),
          dueDate: s(r.due_date),
          bankTotal: opt(r.bank_total),
          bankMin: opt(r.bank_min),
          note: s(r.note),
          fileName: s(r.file_name),
          contentType: s(r.content_type),
          size: Number(r.size) || 0,
          createdAt: s(r.created_at),
          systemTotal: opt(r.system_total),
          systemMin: opt(r.system_min),
          diffTotal: opt(r.diff_total),
          diffMin: opt(r.diff_min),
          match: typeof r.match === 'boolean' ? r.match : null,
        }),
      ),
  })

export type StatementUpload = {
  file: File
  period: string
  statementDate: string
  dueDate: string
  bankTotal: string
  bankMin: string
  note: string
}

export const useUploadBankStatement = (type: StatementTarget, id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (i: StatementUpload) => {
      const form = new FormData()
      form.append('file', i.file)
      form.append('target_type', type)
      form.append('target_id', id)
      form.append('period', i.period)
      form.append('statement_date', i.statementDate)
      form.append('due_date', i.dueDate)
      form.append('bank_total', i.bankTotal.trim())
      form.append('bank_min', i.bankMin.trim())
      form.append('note', i.note.trim())
      return api.post('/api/v1/finance/bank-statements', form)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(type, id) }),
  })
}

export const useDeleteBankStatement = (type: StatementTarget, id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (statementId: string) => api.del(`/api/v1/finance/bank-statements/${statementId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(type, id) }),
  })
}

export const downloadBankStatement = (st: BankStatement) =>
  api.download(`/api/v1/finance/bank-statements/${st.id}/file`, st.fileName || 'ekstre')

export const MAX_STATEMENT_BYTES = 15 * 1024 * 1024
export const STATEMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.csv,.xlsx'
